import { Box, Grid } from "@mui/material";
import React, { useMemo } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/auth";
import { DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER } from "../../../helpers/constants";
import { useAxiosPrivate } from "../../../hooks/useAxiosPrivate";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import {
    documentsApi,
    useDeleteDocumentFromCustomer,
    useListCustomerDocuments,
} from "../../../services/documentsService";
import TableService from "../../../services/tableService";
import BasicDialog from "../../base/BasicDialog";
import AddFileButton from "../../button/AddFileButton/AddFileButton";
import { useDocsTableConfig } from "../../table/DocsTable/TablesConfig";
import FilterTable from "../../table/FilterTable";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import AddEditDocsRestrictedDialog from "../AddEditDocsRestrictedDialog/AddEditDocsRestrictedDialog";

export function ClientFilesDialog(props) {
    const { t } = useTranslation();

    const { openDialog, closeDialog } = useDialogBetter();
    const { user } = useAuth();
    const axiosPrivate = useAxiosPrivate();

    const { data: documents, isLoading, refetch } = useListCustomerDocuments(props.clientId);
    const { getTableProps, fetchingFromBackend } = useDocsTableConfig();
    const { mutate: deleteDocument } = useDeleteDocumentFromCustomer();

    const onPreview = async (docId) => {
        try {
            await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_preview"));
        }
    };

    const onDownloadFile = async (docId) => {
        try {
            await documentsApi.handleDownloadDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_download"));
        }
    };

    const onDeleteFile = (docId) => {
        deleteDocument({ document_id: docId, customer_id: props.clientId });
    };

    const tableProps = useMemo(() => {
        if (fetchingFromBackend) {
            return undefined;
        }
        return getTableProps({ excludedFields: ["manage_products", "investment", "docsinve_is_prospectus"] });
    }, [fetchingFromBackend, getTableProps]);

    const preparedDocsData = useMemo(() => {
        if (!tableProps?.tableConfig || !documents) {
            return undefined;
        }

        return TableService.getPreparedDataForCollapsibleTable(
            tableProps.tableConfig,
            documents,
            [],
            [],
            onPreview,
            onDownloadFile,
            onDeleteFile,
            false, // readOnly
            [],
            refetch,
            false, // showEditButton
            true, // showRevisionButton
            true, // showStandards
            { customer_id: props.clientId }, // // <-- pass context
        );
    }, [tableProps?.tableConfig, documents, refetch, props.clientId]);

    const title = t("clients.ClientFilesDialog.title");

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign="center"
            contentAlign="start"
            title={`${title} ${props.clientName || props.clientId}`}
            maxWidth="xl"
            showDialogActions={false}
        >
            <Box sx={{ minHeight: "40vh", width: "100%" }}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12} sx={{ textAlign: "right" }}>
                        <AddFileButton
                            variant="contained"
                            color="primary"
                            size={"medium"}
                            disabled={props.readOnly}
                            onClick={() =>
                                openDialog({
                                    Component: AddEditDocsRestrictedDialog,
                                    componentProps: {
                                        customer_id: props.clientId,
                                        request_user: user?.username || "",
                                        multiple: true,
                                        showIndustry: true,
                                        availableDocsRestrictedTypes: [
                                            DOCS_RESTRICTED_USER,
                                            DOCS_RESTRICTED_ROLE,
                                        ],
                                        onSubmit: () => {
                                            closeDialog();
                                            refetch();
                                        },
                                    },
                                })
                            }
                            fileType={"file"}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <LoaderWrapper showLoader={isLoading || preparedDocsData === undefined}>
                            {tableProps && (
                                <FilterTable
                                    // @ts-ignore
                                    data={preparedDocsData || []}
                                    withPagination={false}
                                    style={{ maxHeight: "70vh" }}
                                    showCleanFilterIcon={true}
                                    showContextMenu={false}
                                    showExportToFileButton={false}
                                    headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                                        tableProps.tableConfig,
                                        { loading: false, value: {} },
                                        [],
                                        [],
                                    )}
                                    showDetailsIcon={false}
                                    showCounterRecords={false}
                                    showCheckbox={false}
                                    showFilters={true}
                                    filterPrefix={tableProps.tableFilterPrefix}
                                    setSelectedRowId={() => {}}
                                    hideFiltersIconCol={true}
                                />
                            )}
                        </LoaderWrapper>
                    </Grid>
                </Grid>
            </Box>
        </BasicDialog>
    );
}
